import { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminTrailList } from '@/types/admin';

interface SortState {
  field: string;
  direction: 'asc' | 'desc' | null;
}

const SortableHeader: FC<{
  label: string;
  field: string;
  currentSort: SortState;
  onSort: (field: string) => void;
  className?: string;
}> = ({ label, field, currentSort, onSort, className = '' }) => {
  const isActive = currentSort.field === field && currentSort.direction !== null;

  const icon = isActive
    ? currentSort.direction === 'asc'
      ? 'arrow_upward'
      : 'arrow_downward'
    : 'swap_vert';

  return (
    <th
      className={`pb-3 font-medium cursor-pointer select-none hover:text-content-primary transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
        <span>{label}</span>
        <span
          className={`material-symbols-outlined text-base ${
            isActive ? 'text-accent' : 'text-content-muted/50'
          }`}
        >
          {icon}
        </span>
      </div>
    </th>
  );
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const AdminCoursesPage: FC = () => {
  const { showToast } = useToast();
  const [trails, setTrails] = useState<AdminTrailList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [sort, setSort] = useState<SortState>({ field: 'title', direction: 'asc' });

  const getOrderingParam = useCallback((s: SortState): string | undefined => {
    if (!s.direction) return undefined;
    const prefix = s.direction === 'desc' ? '-' : '';
    // Map frontend field names to API field names
    const fieldMap: Record<string, string> = {
      title: 'title',
      lesson_count: 'lesson_count',
      xp_reward: 'xp_reward',
      is_published: 'is_published',
      published_at: 'published_at',
      updated_at: 'updated_at',
    };
    return `${prefix}${fieldMap[s.field] || s.field}`;
  }, []);

  const loadTrails = useCallback(async (currentSort: SortState) => {
    try {
      const ordering = getOrderingParam(currentSort);
      const data = await adminService.getTrails(ordering ? { ordering } : undefined);
      setTrails(data);
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  }, [getOrderingParam]);

  useEffect(() => {
    loadTrails(sort);
  }, [sort, loadTrails]);

  const handleSort = (field: string) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, direction: 'asc' };
      if (prev.direction === 'asc') return { field, direction: 'desc' };
      if (prev.direction === 'desc') return { field: 'title', direction: 'asc' };
      return { field, direction: 'asc' };
    });
  };

  const handleTogglePublish = async (trail: AdminTrailList) => {
    try {
      if (trail.is_published) {
        await adminService.unpublishTrail(trail.id);
      } else {
        await adminService.publishTrail(trail.id);
      }
      await loadTrails(sort);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      showToast(error?.response?.data?.error || 'Erro ao alterar status', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este curso?')) return;
    try {
      await adminService.deleteTrail(id);
      await loadTrails(sort);
    } catch {
      showToast('Erro ao excluir curso', 'error');
    }
  };

  const filteredTrails = trails.filter((t) => {
    if (filter === 'published') return t.is_published;
    if (filter === 'draft') return !t.is_published;
    return true;
  });

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h1 className="text-2xl font-bold">Cursos</h1>
        <Link
          to="/admin/cursos/novo"
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-xl">add</span>
          Novo Curso
        </Link>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-6">
        {(['all', 'published', 'draft'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-accent text-accent-contrast'
                : 'bg-surface-card text-content-muted hover:text-content-primary border border-theme-border'
            }`}
          >
            {f === 'all' ? 'Todos' : f === 'published' ? 'Publicados' : 'Rascunhos'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : filteredTrails.length === 0 ? (
        <p className="text-content-muted">Nenhum curso encontrado.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                <SortableHeader label="Título" field="title" currentSort={sort} onSort={handleSort} />
                <th className="pb-3 font-medium">Tags</th>
                <SortableHeader label="Páginas" field="lesson_count" currentSort={sort} onSort={handleSort} className="text-center" />
                <SortableHeader label="XP" field="xp_reward" currentSort={sort} onSort={handleSort} className="text-center" />
                <SortableHeader label="Status" field="is_published" currentSort={sort} onSort={handleSort} className="text-center" />
                <SortableHeader label="Publicação" field="published_at" currentSort={sort} onSort={handleSort} className="text-center" />
                <SortableHeader label="Atualização" field="updated_at" currentSort={sort} onSort={handleSort} className="text-center" />
                <th className="pb-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrails.map((trail) => (
                <tr key={trail.id} data-testid="course-row" className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                  <td className="py-4 font-medium">{trail.title}</td>
                  <td className="py-4">
                    <div className="flex flex-wrap gap-1">
                      {trail.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: tag.color + '20', color: tag.color }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-4 text-center">{trail.lesson_count}</td>
                  <td className="py-4 text-center">{trail.xp_reward}</td>
                  <td className="py-4 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      trail.is_published
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-yellow-500/10 text-yellow-400'
                    }`}>
                      {trail.is_published ? 'Publicado' : 'Rascunho'}
                    </span>
                  </td>
                  <td className="py-4 text-center text-sm text-content-muted">
                    {trail.published_at ? formatDate(trail.published_at) : <span className="text-yellow-400/70">Rascunho</span>}
                  </td>
                  <td className="py-4 text-center text-sm text-content-muted">
                    {formatDateTime(trail.updated_at)}
                  </td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/admin/cursos/${trail.id}/editar`}
                        className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                        title="Editar"
                        data-testid="edit-course"
                      >
                        <span className="material-symbols-outlined text-xl">edit</span>
                      </Link>
                      <button
                        onClick={() => handleTogglePublish(trail)}
                        className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                        title={trail.is_published ? 'Despublicar' : 'Publicar'}
                      >
                        <span className="material-symbols-outlined text-xl">
                          {trail.is_published ? 'unpublished' : 'publish'}
                        </span>
                      </button>
                      <button
                        onClick={() => handleDelete(trail.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-content-muted hover:text-red-400 transition-colors"
                        title="Excluir"
                        data-testid="delete-course"
                      >
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCoursesPage;
